<script>
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NewTicketModal from './components/NewTicketModal.vue';
import TicketSidePanel from './components/TicketSidePanel.vue';
import StatusDropdown from './components/StatusDropdown.vue';
import { useAlert } from 'dashboard/composables';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { exportTickets } from '../../../api/coLiving/tickets';
import { downloadBlob, todayStamp } from '../../../api/coLiving/files';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import {
  TABS,
  SLA_STATES,
  SOURCES,
  SLA_STYLE,
  statusQuery,
  ticketLocation,
} from './ticketHelpers';
import { TIER_STYLE } from './helpers';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DELAY_MS = 300;
const MEMBER_FILTER_LIMIT = 100;

export default {
  name: 'CoLivingTicketsPage',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    NewTicketModal,
    TicketSidePanel,
    StatusDropdown,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      tabs: TABS,
      slaStates: SLA_STATES,
      sources: SOURCES,
      slaStyle: SLA_STYLE,
      tierStyle: TIER_STYLE,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      tab: 'open',
      search: '',
      condoFilter: '',
      categoryFilter: '',
      slaFilter: '',
      sourceFilter: '',
      memberFilter: '',
      createdFrom: '',
      createdTo: '',
      isExporting: false,
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      loadError: '',
      isNewOpen: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters('coLivingTickets', {
      tickets: 'getTickets',
      meta: 'getMeta',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingCondos', { condos: 'getCondos' }),
    ...mapGetters('coLivingCatalog', { categories: 'getCategories' }),
    ...mapGetters('coLivingTeam', { members: 'getMembers' }),
    ...mapGetters({ currentRole: 'getCurrentRole' }),
    // Agents read the list and the detail only: no New ticket, no defaults.
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    // The open ticket's id lives in the URL (?ticket=), so a refresh or a
    // shared link reopens the panel and Back closes it.
    panelTicketId() {
      const id = this.$route.query && this.$route.query.ticket;
      return typeof id === 'string' ? id : '';
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.meta.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    query() {
      const query = {
        page: this.page,
        page_size: this.pageSize,
        status: statusQuery(this.tab),
      };
      if (this.search.trim()) query.search = this.search.trim();
      if (this.condoFilter) query.site_id = this.condoFilter;
      if (this.categoryFilter) query.category = this.categoryFilter;
      if (this.slaFilter) query.sla = this.slaFilter;
      if (this.sourceFilter) query.source = this.sourceFilter;
      if (this.memberFilter) query.member_id = this.memberFilter;
      if (this.createdFrom) query.created_from = this.createdFrom;
      if (this.createdTo) query.created_to = this.createdTo;
      return query;
    },
    // The server answers 422 invalid_range for this; catching it here gives
    // the message next to the inputs instead of an alert.
    isRangeInvalid() {
      return (
        !!this.createdFrom &&
        !!this.createdTo &&
        this.createdTo < this.createdFrom
      );
    },
  },
  mounted() {
    this.fetchCondos();
    this.fetchCategories();
    if (this.isAdmin) {
      this.fetchMembers({ page_size: MEMBER_FILTER_LIMIT });
    }
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    location: ticketLocation,
    // A room name is shown as it is; the two codes are turned into words.
    placeLabel(place) {
      if (place === 'common_area') {
        return this.$t('CO_LIVING.TICKETS.LOCATION_TYPES.COMMON_AREA');
      }
      if (place === 'whole_unit')
        return this.$t('CO_LIVING.TICKETS.WHOLE_UNIT');
      return place || '-';
    },
    ...mapActions('coLivingTickets', ['fetchTickets']),
    ...mapActions('coLivingCondos', ['fetchCondos']),
    ...mapActions('coLivingCatalog', ['fetchCategories']),
    ...mapActions('coLivingTeam', ['fetchMembers']),
    // Everything the list shows, without paging, as a file for Excel. The
    // proxy drops the file name, so it is named here.
    async exportList() {
      this.isExporting = true;
      try {
        const { page, page_size: pageSize, ...filters } = this.query;
        const { data } = await exportTickets(filters, 'csv');
        downloadBlob(`tickets-${todayStamp()}.csv`, data);
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.TICKETS.EXPORT_FAILED'));
      } finally {
        this.isExporting = false;
      }
    },
    async load() {
      this.loadError = '';
      if (this.isRangeInvalid) return;
      try {
        await this.fetchTickets(this.query);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    setTab(tab) {
      this.tab = tab;
      this.onFilterChange();
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, SEARCH_DELAY_MS);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.onFilterChange();
    },
    // Opens the side panel (pushed, so Back closes it).
    openTicket(ticket) {
      this.$router.push({
        name: 'co_living_tickets',
        params: { accountId: this.$route.params.accountId },
        query: { ...this.$route.query, ticket: ticket.id },
      });
    },
    closePanel() {
      const { ticket, ...rest } = this.$route.query || {};
      this.$router.replace({
        name: 'co_living_tickets',
        params: { accountId: this.$route.params.accountId },
        query: rest,
      });
    },
    openFullPage() {
      this.$router.push({
        name: 'co_living_ticket',
        params: {
          accountId: this.$route.params.accountId,
          ticketId: this.panelTicketId,
        },
      });
    },
    openDefaults() {
      this.$router.push({
        name: 'co_living_ticket_defaults',
        params: { accountId: this.$route.params.accountId },
      });
    },
    onCreated(ticket) {
      if (ticket) this.openTicket(ticket);
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-tickets-page">
    <PageHeader :title="$t('CO_LIVING.TICKETS.TITLE')" icon="ticket" />

    <div class="flex items-start justify-between gap-4 mb-4">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.TICKETS.INTRO') }}
      </p>
      <div v-if="isAdmin" class="flex gap-2 shrink-0">
        <woot-button
          color-scheme="secondary"
          icon="arrow-download"
          :is-loading="isExporting"
          :is-disabled="isExporting"
          data-testid="co-living-ticket-export"
          @click="exportList"
        >
          {{ $t('CO_LIVING.TICKETS.EXPORT') }}
        </woot-button>
        <woot-button
          color-scheme="secondary"
          icon="settings"
          data-testid="co-living-ticket-defaults"
          @click="openDefaults"
        >
          {{ $t('CO_LIVING.TICKETS.DEFAULTS.LINK') }}
        </woot-button>
        <woot-button
          icon="add"
          data-testid="co-living-new-ticket"
          @click="isNewOpen = true"
        >
          {{ $t('CO_LIVING.TICKETS.NEW.BUTTON') }}
        </woot-button>
      </div>
    </div>

    <nav
      class="flex flex-wrap gap-4 mb-4 border-b border-slate-200 dark:border-slate-700"
    >
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="pb-2 text-sm font-medium border-b-2"
        :class="
          tab === t.key
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-500 transition-colors'
        "
        :data-testid="`co-living-ticket-tab-${t.key}`"
        @click="setTab(t.key)"
      >
        {{
          t.key === 'open'
            ? $t('CO_LIVING.TICKETS.TAB_OPEN')
            : $t(`CO_LIVING.TICKETS.STATUS.${t.key}`)
        }}
      </button>
    </nav>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.TICKETS.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-ticket-search"
        @input="onSearchInput"
      />
      <select
        v-model="condoFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-ticket-condo-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TEAM.ALL_CONDOS') }}</option>
        <option v-for="condo in condos" :key="condo.id" :value="condo.id">
          {{ condo.name }}
        </option>
      </select>
      <select
        v-model="categoryFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-ticket-category-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_CATEGORIES') }}</option>
        <option v-for="c in categories" :key="c.key" :value="c.key">
          {{ c.name }}
        </option>
      </select>
      <select
        v-model="slaFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-ticket-sla-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_SLA') }}</option>
        <option v-for="s in slaStates" :key="s" :value="s">
          {{ $t(`CO_LIVING.TICKETS.SLA.${s.toUpperCase()}`) }}
        </option>
      </select>
      <select
        v-model="sourceFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-ticket-source-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_SOURCES') }}</option>
        <option v-for="s in sources" :key="s" :value="s">
          {{ $t(`CO_LIVING.TICKETS.SOURCES.${s.toUpperCase()}`) }}
        </option>
      </select>
      <select
        v-if="isAdmin"
        v-model="memberFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-ticket-member-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TICKETS.ALL_MEMBERS') }}</option>
        <option v-for="m in members" :key="m.id" :value="m.id">
          {{ m.name }}
        </option>
      </select>
      <label class="flex items-center gap-2 text-sm text-slate-600">
        {{ $t('CO_LIVING.TICKETS.CREATED_FROM') }}
        <input
          v-model="createdFrom"
          type="date"
          :max="createdTo || undefined"
          class="!w-40"
          :class="[fieldClass]"
          data-testid="co-living-ticket-created-from"
          @change="onFilterChange"
        />
      </label>
      <label class="flex items-center gap-2 text-sm text-slate-600">
        {{ $t('CO_LIVING.TICKETS.CREATED_TO') }}
        <input
          v-model="createdTo"
          type="date"
          :min="createdFrom || undefined"
          class="!w-40"
          :class="[fieldClass]"
          data-testid="co-living-ticket-created-to"
          @change="onFilterChange"
        />
      </label>
      <p
        v-if="isRangeInvalid"
        class="w-full text-sm text-red-500"
        data-testid="co-living-ticket-range-error"
      >
        {{ $t('CO_LIVING.TICKETS.RANGE_INVALID') }}
      </p>
    </div>

    <LoadingState
      v-if="uiFlags.isFetching && !tickets.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!tickets.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-tickets-empty"
    >
      {{ $t('CO_LIVING.TICKETS.EMPTY') }}
    </p>
    <template v-else>
      <div
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table class="w-full text-sm" data-testid="co-living-tickets-table">
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">{{ $t('CO_LIVING.TICKETS.COLUMNS.TICKET') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.TICKETS.COLUMNS.BUILDING') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.TICKETS.COLUMNS.UNIT') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.TICKETS.COLUMNS.LOCATION') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.TICKETS.COLUMNS.CATEGORY') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TICKETS.COLUMNS.SLA') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.TICKETS.COLUMNS.ASSIGNED') }}
              </th>
              <th class="p-3">
                {{ $t('CO_LIVING.TICKETS.COLUMNS.DAYS_OPEN') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.TICKETS.COLUMNS.SOURCE') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="ticket in tickets"
              :key="ticket.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              :class="{
                'bg-woot-50 dark:bg-slate-700': ticket.id === panelTicketId,
              }"
              :data-testid="`co-living-ticket-row-${ticket.id}`"
              @click="openTicket(ticket)"
            >
              <td class="p-3">
                <button
                  type="button"
                  class="font-medium text-woot-600 dark:text-woot-400 hover:underline"
                  :data-testid="`co-living-ticket-open-${ticket.id}`"
                  @click.stop="openTicket(ticket)"
                >
                  {{ ticket.id }}
                </button>
                <span class="block text-xs text-slate-500">
                  {{ ticket.requester.name || '-' }}
                  <template v-if="ticket.reopen_count">
                    ·
                    {{
                      $t('CO_LIVING.TICKETS.REOPENED_TIMES', {
                        count: ticket.reopen_count,
                      })
                    }}
                  </template>
                </span>
              </td>
              <td class="p-3" data-testid="co-living-ticket-building">
                {{ location(ticket).building || '-' }}
              </td>
              <td class="p-3" data-testid="co-living-ticket-unit">
                {{ location(ticket).unit || '-' }}
              </td>
              <td class="p-3" data-testid="co-living-ticket-place">
                {{ placeLabel(location(ticket).place) }}
              </td>
              <td class="p-3">
                {{ ticket.category_name }}
                <span
                  class="ml-1 px-1.5 py-0.5 text-xs font-medium rounded"
                  :class="tierStyle[ticket.sla_tier]"
                >
                  {{ ticket.sla_tier }}
                </span>
              </td>
              <td class="p-3">
                <StatusDropdown
                  :ticket="ticket"
                  :can-change="isAdmin"
                  @changed="load"
                />
              </td>
              <td class="p-3">
                <span
                  v-if="ticket.sla_state"
                  class="px-2 py-0.5 text-xs font-medium rounded"
                  :class="slaStyle[ticket.sla_state]"
                >
                  {{
                    $t(
                      `CO_LIVING.TICKETS.SLA.${ticket.sla_state.toUpperCase()}`
                    )
                  }}
                </span>
                <span v-else class="text-slate-400">-</span>
                <span
                  v-if="ticket.escalated"
                  class="ml-1 px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
                >
                  {{ $t('CO_LIVING.TICKETS.ESCALATED') }}
                </span>
              </td>
              <td class="p-3">
                {{
                  ticket.assignee
                    ? ticket.assignee.name
                    : $t('CO_LIVING.TICKETS.UNASSIGNED')
                }}
              </td>
              <td class="p-3">
                {{ $t('CO_LIVING.TICKETS.DAYS', { n: ticket.days_open }) }}
              </td>
              <td class="p-3">
                {{
                  $t(`CO_LIVING.TICKETS.SOURCES.${ticket.source.toUpperCase()}`)
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationBar
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        :loading="uiFlags.isFetching"
        @previous="goToPage(page - 1)"
        @next="goToPage(page + 1)"
        @page-size-change="onPageSizeChange"
      />
    </template>

    <NewTicketModal
      v-if="isNewOpen"
      @close="isNewOpen = false"
      @created="onCreated"
    />

    <TicketSidePanel
      v-if="panelTicketId"
      :ticket-id="panelTicketId"
      @close="closePanel"
      @expand="openFullPage"
      @changed="load"
    />
  </div>
</template>
